import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ElementSource, Organization, Profile } from "chq";
import { useProfile } from "app/context";
import { useDoor, useIsMobile } from "app/hooks";
import { getUnsplashRandomImage } from "app/utils/unsplash";
import checkCrossTenant from "app/utils/checkCrossTenant";
import { deepClone, getImage, getSearchValue, t } from "app/utils";
import tagsTypes, { getIcon } from "constants/tagsTypes";
import imagesSizes from "constants/imagesSizes";
import { MENTION_VALUE, SEGMENTED_VALUE } from "constants/mentionConstants";
import { noSlackBroadcastValue } from "components/event/edit/SlackChannelField";
import useTags, { useTagsFromQuery } from "components/post/useTags";
import { CreatomateContext } from "components/stories/video-studio/contexts/VideoStudioContext";
import { SelectedMedia, VideoDimension } from "components/stories/types";
import useHandleSubmit from "../hooks/useHandleSubmit";
import KEYS from "../constants";
import { useHandleMediaChange } from "../hooks";
import { isMediaVideoType } from "../utils";
import {
  PostStoryState,
  PostStoryProviderProps,
  PostStoryLocals,
  MediaData,
  StoryQuestion,
  HandleMediaChange,
  HandleSubmit,
  Media,
  StoryWithNullableQuestion
} from "../types";

const ONE_GIGA = 1024 * 1024 * 1024;
type Value = PostStoryLocals & PostStoryProviderProps;

type PostOption = {
  category: string;
  value: number;
  label: string;
  categoryIcon: string;
};
type PostOptions = PostOption[];

const PostStoryContext = createContext<Value>({
  state: {
    selectedVideosChanged: false,
    needUrlClean: false,
    changed: false,
    userSelectedImage: false,
    selectedMedia: undefined,
    selectedChannel: noSlackBroadcastValue,
    showUnsplashSearch: false,
    selectedOptions: [],
    videoDuration: undefined,
    thumbnail: undefined,
    selectedThumbnailUrl: undefined,
    progress: 0,
    videoDimension: { height: null, width: null },
    postingStory: false,
    selectedQuestion: undefined,
    searchValue: undefined,
    error: undefined,
    selectedPrompt: undefined,
    userAnswers: 0,
    currentStory: undefined,
    currentBody: undefined,
    selectedImages: [],
    mediaData: [],
    indexImageToCrop: -1,
    cropperOpened: undefined,
    webVideoRecorded: false,
    userInformationRequest: false,
    questions: [],
    suggestions: [],
    mappersValueIds: [],
    pausedVideo: false
  },
  setState: () => {},
  open: false,
  canContinueCheck: false,
  editorRef: { current: null },
  editorCopyRef: { current: null },
  aiStoryBodyRef: { current: null },
  selectedOptionsRef: { current: [] },
  onOpen: () => {},
  onConfirmClose: () => {},
  onConfirmCloseAi: () => {},
  onChange: () => {},
  handleSubmit: (_params: HandleSubmit) => {},
  handleOnChangeBody: (_value: string) => {},
  handleMediaChange: (_props: HandleMediaChange) => Promise.resolve(),
  onShareWithChanged: (_newOptions: any[]) => {},
  handleSelectedChannel: (_channel: string) => {},
  handleSelectImage: (_image: SelectedMedia) => {},
  handleFindOnUnsplash: (_value: boolean) => {},
  handleSelectedJobMappings: (_value: Record<string, string[]>) => {},
  handleSelectQuestion: (_question: StoryQuestion | string | undefined) => {},
  handleUserAnswers: (_userAnswers: number) => {},
  getSelectedMediaUrl: (_media: SelectedMedia | null) => null,
  handleMediaEdited: (
    _index: number,
    _value: ElementSource,
    _thumbnail: PostStoryState["thumbnail"]
  ) => {},
  handleRemoveImage: (_indexToRemove: number) => {},
  handleFilesSelected: (_e: React.ChangeEvent<HTMLInputElement>) => {},
  handleCropperClose: () => {},
  handleOpenCropImage: (_imageIndex: number) => {},
  handleVideoEdited: (_thumb: any) => {},
  handleEditQuestion: () => {},
  handleUpdateImages: (_images: PostStoryState["selectedImages"]) => {},
  cleanedMediaData: [],
  isQuickAction: false,
  isTrendingStory: false,
  isProfile: false,
  currentIndex: 0,
  initialOpen: false,
  openModal: false,
  path: "",
  prompts: [],
  queryString: [],
  story: undefined,
  selectedStory: undefined,
  showPoints: false,
  startYourTrend: false,
  organization: undefined,
  isCrossTenant: false,
  onCloseStoryModal: () => {},
  onDeleteStory: () => {},
  onPauseVideo: (value?: boolean) => {},
  onPosted: () => {},
  onPromptChange: () => {},
  onStoryClick: () => {},
  onUpdateStory: () => {},
  onAnotherModalOpen: (_updating: boolean) => {},
  onNotification: () => {},
  restartState: () => {},
  restartSelectedMedia: () => {}
});

export const usePostStoryContext = () => useContext(PostStoryContext);

export const PostStoryProvider: React.FC<PostStoryProviderProps> = ({
  children,
  isQuickAction,
  isTrendingStory,
  isProfile,
  currentIndex,
  initialOpen = false,
  openModal,
  path = "/",
  preFilledQuestion,
  prompts,
  queryString,
  story,
  selectedStory,
  showPoints,
  startYourTrend,
  organization,
  onCloseStoryModal,
  onDeleteStory,
  onPauseVideo,
  onPosted = () => {},
  onPromptChange,
  onStoryClick,
  onUpdateStory,
  onAnotherModalOpen,
  onNotification
}) => {
  const isMobile = useIsMobile();
  const { profile } = useProfile();
  const editorRef = useRef(null);
  const editorCopyRef = useRef(null);
  const aiStoryBodyRef = useRef(null);
  const history = useHistory();
  const location = useLocation();
  const paths = location.pathname.split("/");
  const { isTagEnabled } = useTags(() => {}, true, false);
  const selectedTags = useTagsFromQuery(queryString);
  const currentOrganization = organization || profile.organization;
  const isCrossTenant = checkCrossTenant(organization as Organization, profile as Profile);

  const getMappersValueIds = () => {
    if (story) {
      return story.externalMapperValueAssociations?.map(externalValAssociation => `${externalValAssociation.externalMapperValue.id}`) || [];
    }
    if (preFilledQuestion?.parentStoryQuestion) {
      // if it is in a trend
      return preFilledQuestion.parentStoryQuestion.externalMapperValueAssociations?.map(externalValAssociation => `${externalValAssociation.externalMapperValue.id}`);
    }
    return preFilledQuestion?.externalMapperValueAssociations?.map(externalValAssociation => `${externalValAssociation.externalMapperValue.id}`) || [];
  };

  const initialSelectedPrompt = preFilledQuestion || story?.question;
  const [s, setState] = useState<PostStoryState>(() => ({
    needUrlClean:
      location.pathname === "/"
      || (paths[1] === "new" && paths[2] === "stories"),
    changed: false,
    userSelectedImage: !!story,
    selectedChannel: noSlackBroadcastValue,
    showUnsplashSearch: false,
    selectedOptions: [],
    videoDuration: undefined,
    thumbnail: undefined,
    selectedThumbnailUrl: undefined,
    progress: 0,
    videoDimension: {
      height: null,
      width: null
    },
    postingStory: false,
    selectedQuestion: undefined,
    searchValue: getSearchValue(preFilledQuestion),
    error: undefined,
    selectedPrompt: initialSelectedPrompt,
    userAnswers: 0,
    currentStory: story,
    currentBody: undefined,
    selectedImages: [],
    mediaData: [],
    indexImageToCrop: -1,
    cropperOpened: undefined,
    webVideoRecorded: false,
    userInformationRequest: false,
    questions: [],
    suggestions: [],
    mappersValueIds: getMappersValueIds(),
    pausedVideo: false,
    selectedTemplate: "none"
  }));
  const setNewState = (newState: Partial<PostStoryState>) => setState(
    currentState => ({ ...currentState, ...newState }));

  const {
    open,
    onOpen: onOpenModal,
    onClose: onCloseModal
  } = useDoor(initialOpen);
  const { handleUpdateData, restartData } = useContext(CreatomateContext);

  const selectedOptionsRef = useRef<any[]>();
  const videoDimensionRef = useRef<VideoDimension | undefined>();
  const videoDurationRef = useRef<number>();
  const selectedMediaRef = useRef<SelectedMedia>();
  const searchBarRef = useRef();

  // make stateRef always have the current count
  // your "fixed" callbacks can refer to this object whenever
  // they need the current value.  Note: the callbacks will not
  // be reactive - they will not re-run the instant state changes,
  // but they *will* see the current value whenever they do run
  selectedOptionsRef.current = s.selectedOptions;
  videoDimensionRef.current = s.videoDimension;
  videoDurationRef.current = s.videoDuration;
  selectedMediaRef.current = s.selectedMedia as SelectedMedia;

  useEffect(() => {
    if (!open) return;

    setNewState({
      selectedPrompt: initialSelectedPrompt,
      currentStory: story,
      userSelectedImage: !!story
    });
  }, [story, open, initialSelectedPrompt]);

  useEffect(() => {
    if (!preFilledQuestion) return;

    setNewState({ searchValue: getSearchValue(preFilledQuestion) });
  }, [preFilledQuestion]);

  useEffect(() => {
    let isMounted = true;

    const getRandomImage = async (tag: string) => {
      const result = await getUnsplashRandomImage(tag);
      if (isMounted) setNewState({ selectedMedia: result });
    };

    // eslint-disable-next-line consistent-return
    (async () => {
      if (isTrendingStory && !open && s.searchValue && !s.currentStory) {
        setNewState({
          selectedMedia: (preFilledQuestion?.imageUrl
            || (await getRandomImage(s.searchValue as string))) as SelectedMedia
        });
        return () => {
          isMounted = false;
        };
      }

      if (!isQuickAction && !open && !s.currentStory) {
        setNewState({ selectedMedia: profile.avatar });
        return () => {
          isMounted = false;
        };
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [
    isQuickAction,
    isTrendingStory,
    isMobile,
    open,
    preFilledQuestion,
    s.searchValue,
    s.currentStory,
    profile.avatar
  ]);

  // When the story is present (Edition)
  useEffect(
    () => {
      if (!open) return;

      const getSelectedOptions = () => {
        const postOptions: PostOptions = [];
        if (s.currentStory && s.currentStory.body !== "") {
          const body = JSON.parse(s.currentStory.body as string);
          Object.entries(body.entityMap).forEach(
            ([_, { mutability, type, data }]: [any, any]) => {
              if (
                mutability === SEGMENTED_VALUE
                && type === MENTION_VALUE
                && data.type
                && data.type !== tagsTypes.USERS
                && isTagEnabled(data)
              ) {
                postOptions.push({
                  category: data.type,
                  value: data.entityId,
                  label: data.name,
                  categoryIcon: getIcon(data.type)
                });
              }
            }
          );
        }

        return postOptions;
      };

      if (s.currentStory) {
        const media = s.currentStory.media as Media;
        const opts = getSelectedOptions();
        setNewState({
          selectedOptions: opts,
          selectedMedia: media.url
        });
        if (media.mediaType === KEYS.VIDEO_TYPE) {
          setNewState({ selectedThumbnailUrl: media.thumbnail });
        } else {
          setNewState({ selectedImages: s.currentStory.visuals as Media[] });
        }
      } else {
        setNewState({ selectedMedia: undefined });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [s.currentStory, open]
  );

  useEffect(() => {
    if (
      selectedTags.length !== 0
      && selectedTags.length !== s.selectedOptions?.length
      && s.selectedOptions?.length === 0
    ) {
      setNewState({ selectedOptions: selectedTags });
    }
  }, [selectedTags, s.selectedOptions]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (open) {
      const unblock = history.block(tx => {
        // eslint-disable-next-line no-alert
        const confirm = window.confirm(t("You have unsaved changes, are you sure you'd like to close the story modal?"));
        if (confirm) {
          unblock();
          return undefined;
        }
        return false;
      });
      return () => {
        unblock();
      };
    }
  }, [history, open]);

  const canContinueCheck = !!(
    (s.selectedQuestion || s.selectedPrompt)
    && (s.userSelectedImage || s.selectedMedia)
  );

  const onChange = useCallback(() => setNewState({ changed: true }), []);
  const restartState = useCallback(() => {
    setNewState({
      currentStory: story,
      userSelectedImage: false,
      progress: 0,
      selectedOptions: [],
      changed: false,
      postingStory: false,
      selectedMedia: undefined,
      thumbnail: undefined,
      selectedThumbnailUrl: undefined,
      currentBody: undefined,
      mediaData: [],
      error: undefined,
      selectedImages: [],
      selectedVideos: [],
      webVideoRecorded: false,
      userInformationRequest: false,
      selectedPrompt: initialSelectedPrompt,
      selectedQuestion: undefined
    });
  }, [initialSelectedPrompt, story]);

  const restartSelectedMedia = useCallback(() => setNewState({
    userSelectedImage: false,
    selectedMedia: undefined,
    selectedImages: [],
    mediaData: [],
    thumbnail: undefined,
    selectedThumbnailUrl: undefined,
    webVideoRecorded: false,
    selectedVideos: []
  }), []);

  const onOpen = useCallback(
    (forceOpen = false) => {
      if (onAnotherModalOpen) onAnotherModalOpen(true);
      if (onPauseVideo) onPauseVideo();

      if (forceOpen) {
        onOpenModal();
        return;
      }

      history.push(`stories/trend/${s.selectedPrompt?.id}`);
    },
    [history, onOpenModal, onPauseVideo, onAnotherModalOpen, s.selectedPrompt]
  );

  const onClose = useCallback(() => {
    onCloseModal();
    if (onPauseVideo) onPauseVideo(false);
    if (onCloseStoryModal) onCloseStoryModal();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCloseStoryModal, onCloseModal]);

  const closeModal = useCallback(() => {
    if (!openModal && !s.currentStory && s.needUrlClean) {
      history.push(`${path}${history.location.search}`);
    }
  }, [openModal, s.currentStory, s.needUrlClean, history, path]);

  const onConfirmClose = useCallback(() => {
    const message = t(
      "You have unsaved changes, are you sure you'd like to close the story modal?"
    );
    // eslint-disable-next-line no-alert
    if (!s.changed || window.confirm(message)) {
      closeModal();
      onClose();
      if (onAnotherModalOpen) onAnotherModalOpen(false);
      restartState();
    }
  }, [closeModal, onClose, onAnotherModalOpen, restartState, s.changed]);

  const onConfirmCloseAi = useCallback(() => {
    // eslint-disable-next-line no-alert
    if (!s.changed || window.confirm()) {
      closeModal();
      onClose();
      if (onAnotherModalOpen) onAnotherModalOpen(false);
      restartState();
    }
  }, [closeModal, onClose, onAnotherModalOpen, restartState, s.changed]);

  const handleSubmit = useHandleSubmit({
    videoDurationRef,
    videoDimensionRef,
    editorRef,
    editorCopyRef,
    aiStoryBodyRef,
    selectedOptionsRef,
    prompts,
    state: s,
    setState: setNewState,
    onAnotherModalOpen,
    onNotification,
    onPromptChange,
    onPosted,
    onClose,
    closeModal,
    restartState
  });

  const handleOnChangeBody = useCallback((value: string) => {
    setNewState({ currentBody: value });
  }, []);

  const handleMediaChange = useHandleMediaChange({
    selectedImages: s.selectedImages,
    setState: setNewState,
    state: s,
    onChange
  });

  const onShareWithChanged = useCallback((newOptions: any[]) => {
    setNewState({ changed: true, selectedOptions: newOptions });
  }, []);

  const handleSelectedChannel = useCallback((channel: string) => {
    setNewState({ selectedChannel: channel });
  }, []);

  const handleSelectedJobMappings = useCallback((value: Record<string, string[]>) => {
    setNewState({ mappersValueIds: value.mappersValueIds });
  }, []);

  const handleSelectImage = useCallback(
    (image: SelectedMedia) => {
      if (s.selectedVideos?.length) {
        setNewState(
          {
            selectedMedia: undefined,
            selectedVideos: undefined,
            selectedThumbnailUrl: undefined
          }
        );
        restartState();
        restartData();
        handleUpdateData({ creatomateJson: undefined });
      }

      setNewState({
        selectedMedia: image,
        userSelectedImage: true,
        showUnsplashSearch: false,
        thumbnail: undefined,
        selectedThumbnailUrl: undefined,
        selectedVideos: []
      });
      onChange();
      if (s.selectedImages.length < KEYS.MAX_NUMBER_IMAGES) {
        setNewState({ selectedImages: [...s.selectedImages, image] });
      }
    },
    [handleUpdateData, onChange, restartData, restartState, s.selectedImages, s.selectedVideos]
  );

  const handleFindOnUnsplash = useCallback((value: boolean) => {
    setNewState({
      webVideoRecorded: false,
      videoDuration: undefined,
      showUnsplashSearch: value
    });
  }, []);

  const handleSelectQuestion = useCallback(
    (question?: StoryQuestion) => {
      setNewState({ selectedQuestion: question?.id || question });
      if (question?.id) {
        setNewState({ error: undefined, selectedPrompt: question });
      } else setNewState({ selectedPrompt: undefined });
      if (!s.userSelectedImage) {
        if (question?.storyQuestionTags?.length) {
          const tag = question.storyQuestionTags[0].entity.name;
          setNewState({ searchValue: tag });
        } else {
          setNewState({ searchValue: question?.question || question });
        }
      }

      // the user has selected media
      if (s.selectedImages.length !== 0 || s.selectedVideos?.length !== 0) {
        // early return if the user selects an allow all question
        if (question?.allowedTypes === "all") return;

        // the user selects a just_images question and has selected videos
        if (question?.allowedTypes === "just_images" && s.selectedVideos && s.selectedVideos.length > 0) {
          setNewState({
            error: undefined,
            selectedVideos: [],
            mediaData: [],
            selectedMedia: undefined,
            selectedThumbnailUrl: undefined
          });
        }

        // the user selects a just_videos question and has selected images
        if (question?.allowedTypes === "just_videos" && s.selectedImages.length > 0) {
          setNewState({
            error: undefined,
            userSelectedImage: false,
            selectedImages: [],
            mediaData: [],
            selectedMedia: undefined,
            selectedThumbnailUrl: undefined
          });
        }
      }
    },
    [s.selectedImages.length, s.selectedVideos, s.userSelectedImage]
  );

  const getSelectedMediaUrl = useCallback(
    (media: SelectedMedia) => {
      const mediaToUse = media || s.selectedMedia;
      const image = getImage(imagesSizes.NORMAL, mediaToUse);
      if (image) {
        return image;
      }
      if (isMediaVideoType(mediaToUse as Blob)) {
        return URL.createObjectURL(mediaToUse as Blob);
      }

      return mediaToUse ? URL.createObjectURL(mediaToUse as Blob) : "";
    },
    [s.selectedMedia]
  );

  const handleUserAnswers = useCallback((userAnswers: number) => {
    setNewState({ userAnswers });
  }, []);

  const handleMediaEdited = useCallback(
    (
      index: number,
      value: ElementSource,
      thumbnail: PostStoryState["thumbnail"] = undefined
    ) => {
      const mediaDataArray: MediaData = [...s.mediaData];
      mediaDataArray[index] = value;
      setNewState({ mediaData: mediaDataArray });
      if (thumbnail) {
        setNewState({
          thumbnail,
          selectedThumbnailUrl: thumbnail
        });
      }
    },
    [s.mediaData]
  );

  const handleRemoveImage = useCallback(
    (indexToRemove: number) => {
      const newArray = [...s.selectedImages];
      const newMediaDataArray = [...s.mediaData];
      newArray.splice(indexToRemove, 1);
      newMediaDataArray.splice(indexToRemove, 1);
      setNewState({
        selectedImages: newArray,
        mediaData: newMediaDataArray
      });
      if (newArray.length === 0) {
        setNewState({
          error: undefined,
          selectedMedia: undefined,
          userSelectedImage: false,
          selectedThumbnailUrl: undefined
        });
      }
    },
    [s.mediaData, s.selectedImages]
  );

  const handleFilesSelected = useCallback(
    ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      if (!target.files) return;

      const files = Array.from(target.files);
      // when the user does not select anything
      if (files.length === 0) return;

      const selectedFiles = [...files];
      const justImagesArray = selectedFiles.filter(file => file.type.startsWith("image/"));
      const justVideosArray = selectedFiles.filter(file => file.type.startsWith("video/"));
      const numberOfVideos = s.selectedVideos?.length ?? 0;
      const numberOfFiles = s.selectedImages.length + numberOfVideos + files.length;

      if (numberOfFiles <= KEYS.MAX_NUMBER_IMAGES) {
        setNewState({
          userSelectedImage: true,
          webVideoRecorded: false
        });

        // just one new file
        if (selectedFiles.length === 1) {
          const media = selectedFiles[0];
          const isVideo = isMediaVideoType(media);

          if (isVideo) {
            if (media.size > ONE_GIGA) {
              setNewState({
                error: t("Unfortunately, we are unable to support videos larger than 1GB. Please upload a smaller video."),
                userSelectedImage: false
              });
              return;
            }

            const currentVids = s.webVideoRecorded ? [] : s.selectedVideos ?? [];
            setNewState({
              error: undefined,
              videoDuration: undefined,
              showUnsplashSearch: false,
              thumbnail: undefined,
              selectedThumbnailUrl: undefined,
              selectedMedia: media,
              selectedVideos: isVideo ? [...currentVids, media] : s.selectedVideos,
              selectedVideosChanged: isVideo,
              selectedImages: [],
              mediaData: currentVids.length === 0 ? [] : s.mediaData
            });
          } else if (media.type.startsWith(KEYS.IMAGE_TYPE)) {
            setNewState({
              error: undefined,
              showUnsplashSearch: false,
              selectedMedia: media,
              selectedImages: [...s.selectedImages, media],
              selectedVideos: []
            });
          }
          onChange();
        } else {
          // multiple files
          if (justImagesArray.length > 0 && justVideosArray.length > 0) {
            setNewState({
              error: t("You cannot select videos and images together. Your selected videos have been removed"),
              selectedImages: [...s.selectedImages, ...justImagesArray],
              selectedVideos: [],
              selectedMedia: justImagesArray[0]
            });
            return;
          }

          if (isMobile && justVideosArray.length > 0) {
            setNewState({
              error: t("You cannot select multiple videos on mobile."),
              selectedImages: [],
              selectedVideos: [justVideosArray[0]],
              selectedMedia: justVideosArray[0]
            });
            return;
          }

          const currentVids = s.webVideoRecorded ? [] : s.selectedVideos ?? [];

          setNewState({
            error: undefined,
            selectedMedia: s.selectedMedia ?? selectedFiles[0],
            selectedVideosChanged: true,
            selectedVideos: [...currentVids, ...justVideosArray],
            selectedImages: s.selectedImages
              ? [...s.selectedImages, ...justImagesArray] : justImagesArray
          });

          onChange();
        }
        // eslint-disable-next-line no-param-reassign
        target.value = "";
      } else {
        const mediaType = justVideosArray.length > 9 ? "videos" : "images";
        setNewState({ error: t(`You can not select more than 9 ${mediaType}`) });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, s.mediaData, s.selectedImages, s.selectedMedia, s.selectedVideos]
  );

  const handleCropperClose = useCallback(
    () => setNewState({ cropperOpened: false }),
    []
  );

  const handleOpenCropImage = useCallback((imageIndex: number) => {
    setNewState({ cropperOpened: true, indexImageToCrop: imageIndex });
  }, []);

  const handleVideoEdited = useCallback(async (thumb: any) => {
    if (!selectedMediaRef.current) return;

    setNewState({
      thumbnail: thumb,
      selectedThumbnailUrl: thumb instanceof Blob ? URL.createObjectURL(thumb) : thumb
    });
  }, []);

  const handleEditQuestion = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentStory: {
        ...(prev.currentStory as StoryWithNullableQuestion),
        question: null
      },
      selectedPrompt: undefined
    }));
  }, []);

  const handleUpdateImages = useCallback(
    (images: PostStoryState["selectedImages"]) => {
      setNewState({
        selectedImages: images,
        cropperOpened: false
      });
    },
    []
  );

  const cleanedMediaData = useMemo(
    () => deepClone(s.mediaData).map((data: any) => {
      // eslint-disable-next-line no-param-reassign
      delete data.multipart;
      return data;
    }),
    [s.mediaData]
  );

  const value = useMemo(
    () => ({
      state: s,
      setState: setNewState,
      isQuickAction,
      isTrendingStory,
      isProfile,
      currentIndex,
      initialOpen,
      open,
      openModal,
      path,
      preFilledQuestion,
      prompts,
      queryString,
      story,
      selectedStory,
      showPoints,
      startYourTrend,
      canContinueCheck,
      editorRef,
      editorCopyRef,
      aiStoryBodyRef,
      selectedOptionsRef,
      searchBarRef,
      cleanedMediaData,
      organization: currentOrganization,
      isCrossTenant,
      getSelectedMediaUrl,
      handleSubmit,
      handleOnChangeBody,
      handleMediaChange,
      handleSelectedChannel,
      handleSelectImage,
      handleFindOnUnsplash,
      handleSelectedJobMappings,
      handleSelectQuestion,
      handleUserAnswers,
      handleMediaEdited,
      handleRemoveImage,
      handleFilesSelected,
      handleCropperClose,
      handleOpenCropImage,
      handleVideoEdited,
      handleEditQuestion,
      handleUpdateImages,
      onShareWithChanged,
      onChange,
      onOpen,
      onClose,
      closeModal,
      onConfirmClose,
      onConfirmCloseAi,
      onCloseStoryModal,
      onDeleteStory,
      onPauseVideo,
      onPosted,
      onPromptChange,
      onStoryClick,
      onUpdateStory,
      onAnotherModalOpen,
      onNotification,
      restartState,
      restartSelectedMedia
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      s,
      isQuickAction,
      isTrendingStory,
      isProfile,
      currentIndex,
      initialOpen,
      open,
      openModal,
      path,
      preFilledQuestion,
      prompts,
      queryString,
      story,
      selectedStory,
      showPoints,
      startYourTrend,
      canContinueCheck,
      cleanedMediaData,
      currentOrganization,
      isCrossTenant,
      getSelectedMediaUrl,
      handleSubmit,
      handleOnChangeBody,
      handleMediaChange,
      handleSelectedChannel,
      handleSelectImage,
      handleSelectedJobMappings,
      handleFindOnUnsplash,
      handleSelectQuestion,
      handleUserAnswers,
      handleMediaEdited,
      handleRemoveImage,
      handleFilesSelected,
      handleCropperClose,
      handleOpenCropImage,
      handleVideoEdited,
      handleEditQuestion,
      handleUpdateImages,
      onShareWithChanged,
      onChange,
      onOpen,
      onClose,
      closeModal,
      onConfirmClose,
      onConfirmCloseAi,
      onCloseStoryModal,
      onDeleteStory,
      onPauseVideo,
      onPosted,
      onPromptChange,
      onStoryClick,
      onUpdateStory,
      onAnotherModalOpen,
      onNotification,
      restartState,
      restartSelectedMedia
    ]
  );

  return (
    <PostStoryContext.Provider value={value as Value}>
      {children}
    </PostStoryContext.Provider>
  );
};
